import { type V2FaqListResponseDto, type V2FAQsControllerFetchFaqsParams, useV2FAQsControllerFetchFaqs } from "@everon-it/api-client/contents-gw";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import Table from "@components/atoms/Table";
import ExcelButton from "@components/molecules/ExcelButton";
import Filter from "@components/molecules/Filter";
import { PrivateRoutes } from "@constants/urls";
import { useCascadingCategory } from "@hooks/useCascadingCategory";
import { useFetchFaqCategoryOption } from "@hooks/useFetchFaqCategoryOption";
import useGlobalModal from "@hooks/useGlobalModal";
import { usePagination } from "@hooks/usePagination";
import useRouter from "@hooks/useRouter";
import { colors, typo } from "@styles/index";
import { FilterElementType } from "@utils/DynamicComponentUtils";
import { excelDownload } from "@utils/excel-download";
import { Pagination } from "antd";
import { useFormik } from "formik";
import React from "react";
import styled from "styled-components";
import { htmlToText } from "html-to-text";
import { faqData, type FaqFormType, type FaqInitialValues } from "./Data";

type FaqTableListDataType = {
    rowNumber: number;
} & V2FaqListResponseDto;

const FaqManagement: React.FC = () => {
    // Data
    const filter = faqData.forms;
    const formInitialValues = faqData.formInitialValues;
    const tableContentsInfoData = faqData.tableList;

    // Props
    const MAIN_TITLE = "FAQ 관리";
    const TABLE_TITLE = "FAQ 목록";
    const CREATE_BUTTON_TEXT = "FAQ 등록";
    const FILTER_GIRD_COLUMN = 2;

    // Hook Props
    const DETAIL_PATH = PrivateRoutes.FaqDetail;
    const CREATE_PATH = PrivateRoutes.FaqCreate;

    const { push } = useRouter();
    const { showFaqOrderManagementModal } = useGlobalModal();
    const { page, pageSize, setPage, handlePageSizeChange, resetPage, getRowNumber, searchParams, setSearchParams } = usePagination({ syncWithUrl: true });

    // URL 파라미터에서 초기값 읽기
    const getInitialValues = (): FaqInitialValues => ({
        keyword: searchParams.get("keyword") ?? formInitialValues.keyword,
        isVisible: searchParams.get("isVisible") ?? formInitialValues.isVisible,
        mainCategory: searchParams.get("mainCategory") ?? formInitialValues.mainCategory,
        middleCategory: searchParams.get("middleCategory") ?? formInitialValues.middleCategory,
    });

    // Formik
    const formik = useFormik<FaqInitialValues>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: values => {
            const filteredData: Record<string, string> = {};

            // mainCategory가 변경되었는지 확인 (URL 파라미터와 비교 - useEffect보다 먼저 실행되므로 ref 대신 URL 사용)
            const currentUrlMainCategory = searchParams.get("mainCategory") ?? "";
            const isMainCategoryChanged = currentUrlMainCategory !== "" && currentUrlMainCategory !== values.mainCategory;

            Object.entries(values).forEach(([key, value]) => {
                // mainCategory 변경 시 middleCategory 제외
                if (isMainCategoryChanged && key === "middleCategory") {
                    return;
                }
                if (value !== "") {
                    filteredData[key] = String(value);
                }
            });
            setSearchParams(filteredData);
            resetPage();
        },
    });

    // 검색 필터 초기화
    const handleFormReset = () => {
        setSearchParams({});
        resetPage();
        formik.resetForm();
    };

    // api-client: FAQ 목록 조회 (V2)
    const { data = { rows: [] as V2FaqListResponseDto[], count: 0 } } = useV2FAQsControllerFetchFaqs(
        {
            keyword: formik.values.keyword || undefined,
            isVisible: formik.values.isVisible ? formik.values.isVisible === "true" : undefined,
            mainCategory: (formik.values.mainCategory || undefined) as V2FAQsControllerFetchFaqsParams["mainCategory"],
            middleCategory: (formik.values.middleCategory || undefined) as V2FAQsControllerFetchFaqsParams["middleCategory"],
            page,
            pageSize,
        },
        {
            query: {
                retry: false,
                refetchOnWindowFocus: false,
            },
        },
    );

    const listData = data.rows;
    const totalCount = data.count;

    /** 작성 페이지 이동 */
    const onCreateClick = () => {
        push(CREATE_PATH);
    };

    /** 상세 페이지 이동 */
    const onDetailClick = (uuid: string) => {
        push(`${DETAIL_PATH}/${uuid}`);
    };

    // FAQ 카테고리 조회 (V2)
    const { categoryOption: mainCategoryOption } = useFetchFaqCategoryOption();
    const { categoryOption: middleCategoryOption, setCategoryOption: setMiddleCategoryOption } = useFetchFaqCategoryOption(formik.values.mainCategory);

    // 상위 카테고리 변경 시 하위 카테고리 초기화
    useCascadingCategory({
        formik,
        mainCategoryField: "mainCategory",
        subCategoryField: "middleCategory",
        setSubCategoryOption: setMiddleCategoryOption,
        preserveInitialSubCategory: true,
    });

    const faqFilterData: FaqFormType[] = [
        {
            type: FilterElementType.SELECT,
            name: "mainCategory",
            label: "대분류",
            option: mainCategoryOption,
        },
        {
            type: FilterElementType.SELECT,
            name: "middleCategory",
            label: "중분류",
            option: middleCategoryOption,
        },
        ...filter,
    ];

    const faqCategoryOrderChangeClick = () => {
        showFaqOrderManagementModal({
            title: "전체 탭 순번 관리",
            type: "sortedCategories",
        });
    };

    const faqTableListData: FaqTableListDataType[] = listData.map((item, index) => ({
        ...item,
        rowNumber: getRowNumber(index),
    }));

    const onExcelDownload = () => {
        const header = ["노출 상태", "대분류", "중분류", "제목", "내용", "업데이트 시간", "작성자"];

        const colsWidth = [50, 100, 100, 400, 600, 180, 100];

        const excelData = faqTableListData.map(data => {
            return {
                isVisible: data.isVisible ? "노출" : "비노출",
                mainCategoryDescription: data.mainCategoryDescription,
                middleCategoryDescription: data.middleCategoryDescription,
                title: data.title,
                content: htmlToText(data.content ?? "-"),
                updatedAt: data.updatedAt,
                updatedBy: data.updatedBy,
            };
        });

        excelDownload({
            fileName: "FAQ 관리",
            header,
            data: excelData,
            colsWidth,
        });
    };

    return (
        <ContentsWrap>
            <MainHeaderTitle>{MAIN_TITLE}</MainHeaderTitle>

            <ContentBox>
                <Filter formik={formik} filterData={faqFilterData} girdColumn={FILTER_GIRD_COLUMN} formInitialValues={formInitialValues} handleFormReset={handleFormReset} />
            </ContentBox>

            <ContentBoxWithHeader>
                <TableHeader>
                    <TableTitle>{TABLE_TITLE}</TableTitle>
                    <HeaderButtonWrap>
                        <ExcelButton
                            onClick={() => {
                                onExcelDownload();
                            }}
                        />
                        {/* TODO: 분류탭 순번 변경시 리스트 컨텐츠 재정렬하는 기능 추가 예정, 임시로 v1 사용 */}
                        <Button buttonSize="small" buttonType="line_black" text="질문별 순번 관리" onClick={faqCategoryOrderChangeClick} />
                        <Button buttonSize="small" text={CREATE_BUTTON_TEXT} onClick={onCreateClick} />
                    </HeaderButtonWrap>
                </TableHeader>
                <Table<V2FaqListResponseDto> rowData={faqTableListData} tableContentsInfoData={tableContentsInfoData} currentPageSize={pageSize} onDetailClick={onDetailClick} />
                {/* pageNation */}
                <TableBottomWrap>
                    <ContentsTotal>
                        총<span>{totalCount}</span>
                    </ContentsTotal>
                    <PageNationWrap>
                        <Pagination
                            defaultCurrent={page}
                            defaultPageSize={pageSize}
                            total={totalCount}
                            showSizeChanger
                            locale={{ items_per_page: "개씩 보기" }}
                            current={page}
                            pageSize={pageSize}
                            onShowSizeChange={handlePageSizeChange}
                            onChange={setPage}
                        />
                    </PageNationWrap>
                </TableBottomWrap>
            </ContentBoxWithHeader>
        </ContentsWrap>
    );
};

export default FaqManagement;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

/** 두번째 컨테이너 스타일 */
const ContentBoxWithHeader = styled.div`
    margin-top: 20px;
    padding: 22px 20px;
    background: ${colors.WHITE};
    border-radius: 16px;
    border: 1px solid ${colors.GRAY9};
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
`;

const HeaderButtonWrap = styled.div`
    display: flex;
    max-height: 32px;
    gap: 8px;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;

const PageNationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
